import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageLayout from "../../layouts/PageLayout";
import { formatMoney } from "../Admin/WebOrders/OrderFinancialSummary";
import {
    getSellerOrderHeaders,
    loadSellerCouriers,
    loadSellerOrder,
    sellerOrderApiUrl,
} from "./sellerOrderUtils";
import "../Admin/WebOrders/WebOrderEditPage.css";
import "./StoreSellerOrderEditPage.css";

// Seller version of the admin order details page (Admin/WebOrders/WebOrderEditPage).
// Sellers work in dollars only: no exchange rate and no rupee amounts. They see
// only their own items and the platform fee in dollars on each unit price.

const DELIVERY_STEPS = [
    { key: "Pending", label: "Pending" },
    { key: "Shipped", label: "Shipped" },
    { key: "Delivered", label: "Delivered" },
];

const imageUrl = (path) => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    const baseUrl = process.env.REACT_APP_IMG_URL || "";
    return `${baseUrl.replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
};

const toNumber = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const round2 = (value) => Math.round(value * 100) / 100;

const formatPercent = (value) => `${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

function DeliveryStatusStepper({ status }) {
    const isReturned = status === "Returned";
    const activeIndex = isReturned ? -1 : DELIVERY_STEPS.findIndex((step) => step.key === status);

    return (
        <div className="wo-steps" aria-label={`Delivery status: ${status}`}>
            {DELIVERY_STEPS.map((step, index) => {
                const reached = !isReturned && index <= activeIndex;
                const lineFilled = !isReturned && index < activeIndex;

                return (
                    <React.Fragment key={step.key}>
                        <div className={`wo-step ${reached ? "wo-step--done" : ""}`}>
                            <span className="wo-step__dot">{reached ? "✓" : index + 1}</span>
                            {step.label}
                        </div>
                        {index < DELIVERY_STEPS.length - 1 && (
                            <div className={`wo-steps__line ${lineFilled ? "wo-steps__line--done" : ""}`} />
                        )}
                    </React.Fragment>
                );
            })}
            {isReturned && (
                <>
                    <div className="wo-steps__line" />
                    <div className="wo-step wo-step--returned">
                        <span className="wo-step__dot">↩</span>
                        Returned
                    </div>
                </>
            )}
        </div>
    );
}

function ProductThumb({ src, alt, size = 68, onClick }) {
    const [failed, setFailed] = useState(false);
    const fallback =
        "data:image/svg+xml;utf8," +
        encodeURIComponent(
            `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>
                <rect width='100%' height='100%' fill='#e9ecef'/>
                <text x='50%' y='50%' font-size='8' fill='#6c757d' text-anchor='middle' dominant-baseline='middle'>No Img</text>
             </svg>`
        );

    return (
        <img
            src={failed || !src ? fallback : src}
            alt={alt}
            style={{ width: `${size}px`, height: `${size}px`, objectFit: "contain", flexShrink: 0, cursor: src && !failed ? "pointer" : "default" }}
            onError={() => setFailed(true)}
            onClick={() => src && !failed && onClick && onClick(src)}
        />
    );
}

export default function StoreSellerOrderEditPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [couriers, setCouriers] = useState([]);
    const [formData, setFormData] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [zoom, setZoom] = useState({ show: false, url: "", title: "" });

    // Put an order from the API into the page state (on load and after save).
    const applyOrder = useCallback((loaded, fallbackFee) => {
        const feePercent = loaded.seller_fee_percent ?? fallbackFee ?? null;
        setOrder({ ...loaded, seller_fee_percent: feePercent });
        setFormData({
            delivery_status: loaded.delivery_status || "Pending",
            courier_company_id: loaded.courier_company_id ?? "",
            tracking_number: loaded.tracking_number || "",
            delivery_days: loaded.delivery_days ?? "",
        });
        setProducts((loaded.products || []).map((line) => ({
            ...line,
            fee_percent: line.fee_percent ?? line.payout?.fee_percent ?? feePercent ?? 0,
            net_weight: line.net_weight ?? "",
            declared_value: line.declared_value ?? "",
        })));
    }, []);

    useEffect(() => {
        let active = true;

        const loadPage = async () => {
            setLoading(true);
            setError("");
            try {
                const [loaded, courierList] = await Promise.all([loadSellerOrder(id), loadSellerCouriers()]);
                if (!active) return;

                const matchedCourier = courierList.find((courier) => courier.company_name === loaded.courier_company);
                setCouriers(courierList);
                applyOrder({ ...loaded, courier_company_id: loaded.courier_company_id || matchedCourier?.id || "" });
            } catch (requestError) {
                if (active) {
                    setError(requestError.response?.data?.message || requestError.message || "Could not load this order.");
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        loadPage();
        return () => {
            active = false;
        };
    }, [id, applyOrder]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((current) => ({ ...current, [name]: value }));
        setFieldErrors((current) => ({ ...current, [name]: undefined }));
        setSuccessMsg("");
    };

    const handleProductChange = (index, field, value) => {
        setProducts((current) => current.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
        setFieldErrors((current) => ({ ...current, [`order_details.${index}.${field}`]: undefined }));
        setSuccessMsg("");
    };

    const fieldError = (key) => {
        const message = fieldErrors[key];
        return Array.isArray(message) ? message[0] : message;
    };

    const saveOrder = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        setSuccessMsg("");
        setFieldErrors({});

        const payload = {
            delivery_status: formData.delivery_status,
            tracking_number: formData.tracking_number || null,
            courier_company_id: formData.courier_company_id || null,
            ...(formData.delivery_days !== "" ? { delivery_days: Number(formData.delivery_days) } : {}),
            order_details: products.map((line) => ({
                id: line.id,
                ...(line.net_weight !== "" ? { net_weight: Number(line.net_weight) } : {}),
                ...(line.declared_value !== "" ? { declared_value: Number(line.declared_value) } : {}),
            })),
        };

        try {
            const response = await axios.put(`${sellerOrderApiUrl}/store-seller/update-order/${id}`, payload, {
                headers: getSellerOrderHeaders(),
            });
            if (response.data?.data) {
                applyOrder(response.data.data, order?.seller_fee_percent);
            }
            setSuccessMsg("Order saved. Your payout has been recalculated.");
        } catch (requestError) {
            if (requestError.response?.status === 422 && requestError.response?.data?.errors) {
                setFieldErrors(requestError.response.data.errors);
                setError("Please fix the highlighted fields.");
            } else {
                setError(requestError.response?.data?.message || "Could not update this order.");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading order...</p>
                </div>
            </PageLayout>
        );
    }

    if (!order || !formData) {
        return (
            <PageLayout>
                <Alert variant="danger">{error || "Order details are not available."}</Alert>
                <Button variant="secondary" onClick={() => navigate("/seller-store-orders")}>Back to orders</Button>
            </PageLayout>
        );
    }

    // ---- Money: dollars only, platform fee on each unit price ----
    const lines = products.map((line) => {
        const quantity = Number(line.quantity) || 1;
        const unitPrice = toNumber(line.unit_price) ?? 0;
        const percent = Number(line.fee_percent) || 0;
        const grossUsd = round2(unitPrice * quantity);
        const feeUsd = round2((grossUsd * percent) / 100);
        const netUsd = round2(grossUsd - feeUsd);
        return { ...line, quantity, unitPrice, percent, grossUsd, feeUsd, netUsd };
    });

    const totals = lines.reduce(
        (sum, line) => ({
            gross: sum.gross + line.grossUsd,
            fee: sum.fee + line.feeUsd,
            net: sum.net + line.netUsd,
            shipping: sum.shipping + (Number(line.payout?.shipping_deduction_usd) || 0),
            other: sum.other + (Number(line.payout?.other_deductions_usd) || 0),
        }),
        { gross: 0, fee: 0, net: 0, shipping: 0, other: 0 }
    );
    const percents = [...new Set(lines.map((line) => line.percent))];
    const feeLabel = percents.length === 1 ? `Platform fee (${formatPercent(percents[0])})` : "Platform fee";
    const receiveUsd = round2(totals.net - totals.shipping - totals.other);

    const orderedAt = order.ordered_at ? new Date(order.ordered_at.replace(" ", "T")) : null;
    const selectedCourier = couriers.find((courier) => String(courier.id) === String(formData.courier_company_id));
    const buyer = order.buyer || {};
    const storage = order.seller_storage_preference;

    return (
        <PageLayout>
            <div className="wo-page sso-page">
                <div className="mc-card mb-4">
                    <div className="mc-breadcrumb">
                        <h3 className="mc-breadcrumb-title">Edit order {order.order_number || `#${id}`}</h3>
                        <ul className="mc-breadcrumb-list">
                            <li className="mc-breadcrumb-item"><Link to="/dashboard" className="mc-breadcrumb-link">Home</Link></li>
                            <li className="mc-breadcrumb-item"><Link to="/seller-store-orders" className="mc-breadcrumb-link">My Orders</Link></li>
                            <li className="mc-breadcrumb-item">Edit</li>
                        </ul>
                    </div>
                </div>

                {/* Order header */}
                <div className="wo-header">
                    <div className="wo-header__top">
                        <div>
                            <h2 className="wo-header__number">{order.order_number || `#${id}`}</h2>
                            <div className="wo-header__meta">
                                {order.payment_status && <span className="wo-chip">Payment: {order.payment_status}</span>}
                                {lines.map((line) => line.product?.product_code).filter(Boolean).map((code) => (
                                    <span key={code} className="wo-chip wo-chip--code">{code}</span>
                                ))}
                                {storage && (
                                    <span className="wo-chip wo-chip--seller">
                                        {storage === "admin_store" ? "Kept in Watches Pool store" : "Kept in your own store"}
                                    </span>
                                )}
                                {orderedAt && !Number.isNaN(orderedAt.getTime()) && (
                                    <span className="wo-chip wo-chip--quiet">
                                        Ordered {orderedAt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="wo-header__actions">
                            <Button className="wo-btn-quiet" as={Link} to={`/seller-store-orders/${id}`}>View order</Button>
                            <Button className="wo-btn-quiet" onClick={() => navigate("/seller-store-orders")}>Back to orders</Button>
                        </div>
                    </div>
                    <DeliveryStatusStepper status={formData.delivery_status} />
                </div>

                <Form onSubmit={saveOrder} noValidate>
                    <Row className="g-4">
                        {/* Left: products and payout */}
                        <Col lg={7}>
                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <div>
                                        <h3 className="wo-panel__title">Your products in this order</h3>
                                        <p className="wo-panel__hint">
                                            {lines.length} {lines.length === 1 ? "item" : "items"}. All amounts are in US dollars; the platform fee is taken from each unit price.
                                        </p>
                                    </div>
                                </header>

                                {lines.length === 0 ? (
                                    <div className="wo-panel__body text-muted">No products of yours in this order.</div>
                                ) : (
                                    <ul className="wo-products">
                                        {lines.map((line, index) => {
                                            const title = line.product?.title || "Product";
                                            const src = imageUrl(line.product?.card_img);

                                            return (
                                                <li key={line.id} className="wo-product">
                                                    <div className="wo-product__thumb">
                                                        <ProductThumb
                                                            src={src}
                                                            alt={title}
                                                            onClick={(url) => setZoom({ show: true, url, title })}
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="wo-product__top">
                                                            {line.product?.product_code && (
                                                                <span className="wo-chip wo-chip--code">{line.product.product_code}</span>
                                                            )}
                                                            {line.payout?.status_label && (
                                                                <span className={`wo-chip sso-payout sso-payout--${line.payout.status}`}>
                                                                    Payout: {line.payout.status_label}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="wo-product__title">
                                                            {line.product?.id ? (
                                                                <Link to={`/store-seller/product-view/${line.product.id}`}>{title}</Link>
                                                            ) : (
                                                                title
                                                            )}
                                                        </div>

                                                        <dl className="wo-product__facts">
                                                            <div>
                                                                <dt>Unit price</dt>
                                                                <dd className="wo-num">{formatMoney(line.unitPrice, "USD")}</dd>
                                                            </div>
                                                            {line.quantity > 1 && (
                                                                <div>
                                                                    <dt>Quantity</dt>
                                                                    <dd className="wo-num">{line.quantity}</dd>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <dt>Platform fee ({formatPercent(line.percent)})</dt>
                                                                <dd className="wo-num sso-fee">− {formatMoney(line.feeUsd, "USD")}</dd>
                                                            </div>
                                                            <div>
                                                                <dt>You receive</dt>
                                                                <dd className="wo-num sso-net">{formatMoney(line.netUsd, "USD")}</dd>
                                                            </div>
                                                        </dl>

                                                        <div className="wo-product__inputs sso-inputs">
                                                            <Form.Group controlId={`sso-declared-${line.id}`}>
                                                                <Form.Label>Declared value ($)</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={line.declared_value}
                                                                    onChange={(event) => handleProductChange(index, "declared_value", event.target.value)}
                                                                    isInvalid={!!fieldError(`order_details.${index}.declared_value`)}
                                                                />
                                                                <Form.Control.Feedback type="invalid">{fieldError(`order_details.${index}.declared_value`)}</Form.Control.Feedback>
                                                            </Form.Group>
                                                            <Form.Group controlId={`sso-weight-${line.id}`}>
                                                                <Form.Label>Net weight (kg)</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={line.net_weight}
                                                                    onChange={(event) => handleProductChange(index, "net_weight", event.target.value)}
                                                                    isInvalid={!!fieldError(`order_details.${index}.net_weight`)}
                                                                />
                                                                <Form.Control.Feedback type="invalid">{fieldError(`order_details.${index}.net_weight`)}</Form.Control.Feedback>
                                                            </Form.Group>
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                <div className="wo-totals">
                                    <div><span>Your products</span><strong>{formatMoney(totals.gross, "USD")}</strong></div>
                                    <div><span>{feeLabel}</span><strong className="sso-fee">− {formatMoney(totals.fee, "USD")}</strong></div>
                                    <div><span>You receive</span><strong>{formatMoney(receiveUsd, "USD")}</strong></div>
                                </div>
                            </section>

                            {/* Payout summary */}
                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <div>
                                        <h3 className="wo-panel__title">Your payout</h3>
                                        <p className="wo-panel__hint">In US dollars.</p>
                                    </div>
                                </header>
                                <div className="wo-panel__body">
                                    <div style={{ overflowX: "auto" }}>
                                        <table className="wo-ledger">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Details</th>
                                                    <th scope="col">USD</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Sale of your products<span className="wo-ledger__note">Unit price × quantity</span></td>
                                                    <td className="wo-ledger__amt">{formatMoney(totals.gross, "USD")}</td>
                                                </tr>
                                                <tr className="wo-ledger--deduct">
                                                    <td>− {feeLabel}<span className="wo-ledger__note">Watches Pool fee on each unit price</span></td>
                                                    <td className="wo-ledger__amt">{formatMoney(totals.fee, "USD")}</td>
                                                </tr>
                                                {totals.shipping > 0 && (
                                                    <tr className="wo-ledger--deduct">
                                                        <td>− Shipping<span className="wo-ledger__note">Order shipping taken from your share</span></td>
                                                        <td className="wo-ledger__amt">{formatMoney(totals.shipping, "USD")}</td>
                                                    </tr>
                                                )}
                                                {totals.other > 0 && (
                                                    <tr className="wo-ledger--deduct">
                                                        <td>− Other deductions</td>
                                                        <td className="wo-ledger__amt">{formatMoney(totals.other, "USD")}</td>
                                                    </tr>
                                                )}
                                                <tr className="wo-ledger--subtotal">
                                                    <td>You receive</td>
                                                    <td className="wo-ledger__amt text-success">{formatMoney(receiveUsd, "USD")}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="sso-footnote">
                                        Payouts are released after the hold period. See <Link to="/seller-payouts">My Payouts</Link> for the status of each item.
                                    </p>
                                </div>
                            </section>
                        </Col>

                        {/* Right: delivery, shipment, buyer */}
                        <Col lg={5}>
                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <h3 className="wo-panel__title">Delivery</h3>
                                </header>
                                <div className="wo-panel__body">
                                    <Row className="g-3">
                                        <Col sm={6}>
                                            <Form.Group controlId="sso-delivery-status">
                                                <Form.Label>Status</Form.Label>
                                                <Form.Select name="delivery_status" value={formData.delivery_status} onChange={handleChange}>
                                                    <option value="Pending">Pending</option>
                                                    <option value="Shipped">Shipped</option>
                                                    <option value="Delivered">Delivered</option>
                                                    <option value="Returned">Returned</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="sso-courier">
                                                <Form.Label>Courier</Form.Label>
                                                <Form.Select name="courier_company_id" value={formData.courier_company_id} onChange={handleChange}>
                                                    <option value="">Select courier</option>
                                                    {couriers.map((courier) => (
                                                        <option key={courier.id} value={courier.id}>{courier.company_name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="sso-tracking">
                                                <Form.Label>Tracking number</Form.Label>
                                                <Form.Control name="tracking_number" value={formData.tracking_number} onChange={handleChange} />
                                            </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                            <Form.Group controlId="sso-days">
                                                <Form.Label>Delivery days</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    name="delivery_days"
                                                    value={formData.delivery_days}
                                                    onChange={handleChange}
                                                    isInvalid={!!fieldError("delivery_days")}
                                                />
                                                <Form.Control.Feedback type="invalid">{fieldError("delivery_days")}</Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>
                            </section>

                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <h3 className="wo-panel__title">Shipment</h3>
                                </header>
                                <div className="wo-panel__body">
                                    <div className="wo-ship__line">
                                        <span>Courier</span>
                                        <strong>{selectedCourier ? selectedCourier.company_name : "Not selected"}</strong>
                                    </div>
                                    <div className="wo-ship__line">
                                        <span>Tracking number</span>
                                        <strong>{formData.tracking_number || "Not added"}</strong>
                                    </div>
                                </div>
                            </section>

                            <section className="wo-panel">
                                <header className="wo-panel__head">
                                    <h3 className="wo-panel__title">Buyer &amp; shipping address</h3>
                                </header>
                                <div className="wo-panel__body">
                                    <div className="wo-ship__line"><span>Name</span><strong>{buyer.name || "—"}</strong></div>
                                    <div className="wo-ship__line"><span>Phone</span><strong>{buyer.phone || "—"}</strong></div>
                                    <div className="wo-ship__line"><span>Email</span><strong className="text-break">{buyer.email || "—"}</strong></div>
                                    <div className="wo-ship__line"><span>Country</span><strong>{[buyer.city, buyer.country].filter(Boolean).join(", ") || "—"}</strong></div>
                                    <div className="sso-address">{buyer.address || "No address"}</div>
                                </div>
                            </section>
                        </Col>
                    </Row>

                    <div className="wo-savebar">
                        <span
                            className={`wo-savebar__msg ${error ? "wo-savebar__msg--error" : successMsg ? "wo-savebar__msg--ok" : ""}`}
                            role="status"
                            aria-live="polite"
                        >
                            {error || successMsg || ""}
                        </span>
                        <Button className="wo-btn-quiet" onClick={() => navigate("/seller-store-orders")}>Cancel</Button>
                        <Button type="submit" className="wo-btn-primary" disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner size="sm" animation="border" className="me-2" />
                                    Saving
                                </>
                            ) : (
                                "Save order"
                            )}
                        </Button>
                    </div>
                </Form>
            </div>

            <Modal show={zoom.show} onHide={() => setZoom({ show: false, url: "", title: "" })} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0">{zoom.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-3 bg-dark rounded-bottom">
                    <img src={zoom.url} alt={zoom.title} className="img-fluid rounded" style={{ maxHeight: "80vh", objectFit: "contain" }} />
                </Modal.Body>
            </Modal>
        </PageLayout>
    );
}
